import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import { webLog } from '../../../shared/api/grpc/webLogger';
import {
  buildValidationMappingOverridesFromStore,
  mappingConfigsHaveSameEnabledBindings,
  mergeMappingConfigWithOverrides,
  toHostConfigCandidate,
  type HostPcfMappingConfig,
} from './validationMappingOverrides';

export interface ValidationMappingSyncOptions {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export interface ValidationMappingSyncResult {
  success: boolean;
  error?: string;
}

export async function syncValidationMappingsToHost(
  call: (method: string, params?: Record<string, unknown>) => Promise<any>,
  overrides: Record<string, string> = buildValidationMappingOverridesFromStore(),
  options: ValidationMappingSyncOptions = {}
): Promise<ValidationMappingSyncResult> {
  try {
    if (Object.keys(overrides).length === 0) {
      return { success: true };
    }

    const currentMappingsRaw = await call('pcfMapping.getAll');
    const currentMappings = toHostConfigCandidate(currentMappingsRaw);
    if (!currentMappings) {
      return {
        success: false,
        error: 'The current host mapping configuration could not be read.',
      };
    }
    const mergedConfig = mergeMappingConfigWithOverrides(currentMappings, overrides);
    webLog.info(
      'validation-preflight',
      `mapping-sync: currentMappings=${currentMappings?.Mappings?.length ?? 0} mergedMappings=${mergedConfig.Mappings.length} overrideKeys=${Object.keys(overrides).join(',')}`
    );

    if (mappingConfigsHaveSameEnabledBindings(currentMappings, mergedConfig)) {
      webLog.info(
        'validation-preflight',
        'mapping-sync: skipped save because host bindings already match current settings'
      );
      return { success: true };
    }

    const saveResult = await call('pcfMapping.save', {
      mappings: JSON.stringify(mergedConfig),
      onProgress: options.onProgress,
    });

    const saveSucceeded =
      saveResult === true ||
      saveResult?.IsSuccess === true ||
      saveResult?.isSuccess === true ||
      saveResult?.success === true;

    if (!saveSucceeded) {
      return {
        success: false,
        error: String(saveResult?.Error || saveResult?.error || 'pcfMapping.save failed'),
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export type { HostPcfMappingConfig };
